import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
} from "@mui/material";
import {
  KeyboardArrowLeft,
  KeyboardArrowRight,
  FirstPage,
  LastPage,
} from "@mui/icons-material";
import {
  fetchRecruiterInformation,
  selectFilteredRecruiters,
  selectRecruitersLoading,
  selectRecruitersError,
  selectTotalRecruiters,
  setSearchQuery,
  setFilters,
  clearFilters,
} from "../redux/slices/recruitersTableSlice";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward"; // Import arrow icon

const RecruitersTable = () => {
  const dispatch = useDispatch();

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;

  // Select data from store
  const recruiters = useSelector(selectFilteredRecruiters);
  const loading = useSelector(selectRecruitersLoading);
  const error = useSelector(selectRecruitersError);
  const totalRecruiters = useSelector(selectTotalRecruiters);

  // Calculate pagination
  const totalPages = Math.ceil(recruiters.length / recordsPerPage);
  const startIndex = (currentPage - 1) * recordsPerPage;
  const endIndex = startIndex + recordsPerPage;
  const currentRecruiters = recruiters.slice(startIndex, endIndex);

  // Fetch data on component mount
  useEffect(() => {
    dispatch(fetchRecruiterInformation());
  }, [dispatch]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [recruiters.length]);

  // Handle search
  const handleSearch = (query) => {
    dispatch(setSearchQuery(query));
  };

  // Handle filters
  const handleFilter = (filterType, value) => {
    dispatch(setFilters({ [filterType]: value }));
  };

  // Clear all filters
  const handleClearFilters = () => {
    dispatch(clearFilters());
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    if (totalPages <= maxVisiblePages) {
      // Show all pages if total pages is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Show first page, current page with neighbors, and last page
      pages.push(1);

      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);

      if (startPage > 2) {
        pages.push("...");
      }

      for (let i = startPage; i <= endPage; i++) {
        if (i !== 1 && i !== totalPages) {
          pages.push(i);
        }
      }

      if (endPage < totalPages - 1) {
        pages.push("...");
      }

      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }

    return pages;
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "200px",
          ml: "220px",
        }}
      >
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading recruiters...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ ml: "220px", p: 2 }}>
        <Alert severity="error">Error: {error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ ml: "220px", p: 2 }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{ mb: 3, fontWeight: 700, fontFamily: "Outfit", mt: "2%" }}
      >
        Recruiters Information
      </Typography>

      {/* Search and Filters */}
      <Box
        sx={{
          display: "flex",
          gap: 2,
          mb: 3,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        <TextField
          label="Search recruiters..."
          variant="outlined"
          size="small"
          sx={{ minWidth: 200 }}
          onChange={(e) => handleSearch(e.target.value)}
        />

        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Profile Status</InputLabel>
          <Select
            label="Profile Status"
            onChange={(e) =>
              handleFilter(
                "profileCompleted",
                e.target.value === "true"
                  ? true
                  : e.target.value === "false"
                  ? false
                  : null
              )
            }
            defaultValue=""
          >
            <MenuItem value="">All Profiles</MenuItem>
            <MenuItem value="true">True</MenuItem>
            <MenuItem value="false">False</MenuItem>
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          color="secondary"
          onClick={handleClearFilters}
          size="small"
        >
          Clear Filters
        </Button>
      </Box>

      {/* Table */}
      <TableContainer component={Paper} elevation={2}>
        <Table sx={{ minWidth: 650 }} aria-label="recruiters table">
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell sx={{ fontWeight: "bold" }}>Name</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Recruiter Email</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Company Name</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>
                Recruiter Location
              </TableCell>

              <TableCell sx={{ fontWeight: "bold" }}>Phone</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Job Title</TableCell>
              {/* <TableCell sx={{ fontWeight: "bold" }}>Profile Status</TableCell> */}
              <TableCell sx={{ fontWeight: "bold" }}>Company Details</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {currentRecruiters.map((recruiter) => (
              <TableRow
                key={String(recruiter._id)}
                sx={{
                  "&:last-child td, &:last-child th": { border: 0 },
                  "&:hover": { backgroundColor: "#f9f9f9" },
                }}
              >
                <TableCell
                  component="th"
                  scope="row"
                  sx={{ color: "rgb(144, 72, 251)" }}
                >
                  {String(recruiter.name)}
                </TableCell>
                <TableCell>{String(recruiter.email)}</TableCell>
                <TableCell>{String(recruiter.companyName)}</TableCell>
                <TableCell>{String(recruiter.recruiterLocation)}</TableCell>
                <TableCell>{String(recruiter.phone)}</TableCell>
                <TableCell>{String(recruiter.jobTitle)}</TableCell>
                {/* <TableCell>
                  <Chip
                    label={recruiter.profileCompleted ? "True" : "False"}
                    sx={{
                      backgroundColor: recruiter.profileCompleted
                        ? "#05CD99"
                        : "#FF6767",
                      color: "white",
                      fontWeight: "500",
                    }}
                    size="small"
                  />
                </TableCell> */}
                <TableCell
                  sx={{
                    p: { xs: "12px 8px", sm: "16px 12px", md: "20px" },
                    textAlign: "center",
                  }}
                >
                  <Button
                    onClick={() => {
                      console.log("Company ID:", recruiter.id);
                      // Store company data in localStorage for persistence
                      localStorage.setItem(
                        `companyData_${recruiter.id}`, // Changed from recruiter.id.id to recruiter.id
                        JSON.stringify({
                          companyId: recruiter.id,
                          company: recruiter,
                          timestamp: Date.now(),
                        })
                      );

                      const url = `/TwoTabsPage?companyId=${recruiter.id}`;
                      window.open(url, "_blank");
                    }}
                    style={{
                      backgroundColor: "#9048FB",
                      color: "white",
                      border: "none",
                      padding: "8px 16px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      fontSize: "12px",
                      fontWeight: "600",
                      fontFamily: "Outfit",
                      transition: "background-color 0.2s ease",
                      whiteSpace: "nowrap",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: "30px",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = "#7c3aed";
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = "#9048FB";
                    }}
                  >
                    <ArrowForwardIcon fontSize="small" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Custom Pagination */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 3,
            px: 2,
          }}
        >
          {/* Results Info */}
          <Typography variant="body2" color="text.secondary">
            Showing {startIndex + 1} to {Math.min(endIndex, recruiters.length)}{" "}
            of {recruiters.length} companies
          </Typography>

          {/* Pagination Controls */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {/* Previous Button */}
            <IconButton
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              sx={{
                color: currentPage === 1 ? "#ccc" : "#666",
                "&:hover": {
                  backgroundColor:
                    currentPage === 1 ? "transparent" : "#f5f5f5",
                },
              }}
            >
              <KeyboardArrowLeft />
            </IconButton>

            {/* Page Numbers */}
            {getPageNumbers().map((page, index) => (
              <React.Fragment key={index}>
                {page === "..." ? (
                  <Typography variant="body2" sx={{ px: 1, color: "#666" }}>
                    ...
                  </Typography>
                ) : (
                  <Button
                    variant={currentPage === page ? "contained" : "text"}
                    onClick={() => handlePageChange(page)}
                    sx={{
                      minWidth: "40px",
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%", // Make it circular
                      fontSize: "14px",
                      fontWeight: currentPage === page ? "600" : "400",
                      backgroundColor:
                        currentPage === page ? "#8B5CF6" : "transparent",
                      color: currentPage === page ? "white" : "#666",
                      "&:hover": {
                        backgroundColor:
                          currentPage === page ? "#7C3AED" : "#f5f5f5",
                      },
                    }}
                  >
                    {page}
                  </Button>
                )}
              </React.Fragment>
            ))}

            {/* Next Button */}
            <IconButton
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              sx={{
                color: currentPage === totalPages ? "#ccc" : "#666",
                "&:hover": {
                  backgroundColor:
                    currentPage === totalPages ? "transparent" : "#f5f5f5",
                },
              }}
            >
              <KeyboardArrowRight />
            </IconButton>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default RecruitersTable;
